/* =============================================
   Gallery Size Filter — Studio Ode (P01-BLOCK-07)

   When a customer selects a size variant, filter the product media
   gallery so only images tagged with that size remain visible.
   Untagged images (lifestyle / detail shots) are always visible.

   Size tag source:
     Each slide carries `data-ode-size-tag="<size label>"` which is
     written server-side by `main-product-section.liquid` from the
     media's original alt text (format: "description | size:X").
     (The <img>'s rendered alt is already stripped of the tag by
     `product-thumbnail.liquid`, so we cannot read it from there.)

   Swiper integration:
     - Slides are REMOVED from the DOM (not display:none) so Swiper's
       internal slide array + thumbs-main linkage stay consistent.
     - After removal we call `swiper.update()`, which rebuilds
       `swiper.slides` from the current DOM.
     - We then scroll the main slider to the variant's featured media
       (via `data-media-id`), with a fallback to slide 0.
     - For the thumbs-to-main linkage, Swiper v8 tracks active slide
       by DOM position, so as long as we keep thumbs + main filtered
       to the SAME set of media IDs, clicking a thumb correctly
       scrolls the main to the matching slide.
   ============================================= */

(function () {
  'use strict';

  function matchesSize(sizeTag, selectedSize) {
    if (!sizeTag) return true;              /* untagged = always visible */
    if (!selectedSize) return true;         /* no variant yet — show all */
    return sizeTag.toLowerCase().indexOf(String(selectedSize).toLowerCase()) === 0;
  }

  function variantSize(variant) {
    if (!variant) return null;
    /* Size is the primary option for Studio Ode bouquets. */
    return variant.option1 || (variant.options && variant.options[0]) || variant.title || null;
  }

  function cacheSlides(wrapper, slideSelector) {
    if (!wrapper) return [];
    return Array.prototype.slice.call(wrapper.querySelectorAll(slideSelector)).map(function (el, i) {
      return {
        el: el,
        parent: el.parentElement,
        originalIndex: i,
        sizeTag: el.getAttribute('data-ode-size-tag') || null,
        mediaId: el.getAttribute('data-media-id') || el.getAttribute('data-media-sub-id') || null
      };
    });
  }

  function reinsertInOrder(slide, cachedList) {
    if (slide.el.isConnected) return;
    var next = null;
    for (var i = 0; i < cachedList.length; i++) {
      var other = cachedList[i];
      if (other === slide) continue;
      if (other.originalIndex > slide.originalIndex && other.el.isConnected) {
        next = other.el; break;
      }
    }
    if (next) slide.parent.insertBefore(slide.el, next);
    else slide.parent.appendChild(slide.el);
  }

  function applyFilter(cachedList, size) {
    cachedList.forEach(function (s) {
      if (matchesSize(s.sizeTag, size)) {
        reinsertInOrder(s, cachedList);
      } else if (s.el.isConnected) {
        s.el.parentNode.removeChild(s.el);
      }
    });
  }

  /* Read current size from the Studio Ode size label span
     (set by main-product-section.liquid: `data-option-name="Size"`). */
  function readInitialSize(section) {
    var label = section.querySelector('.ode-size-label');
    if (label) {
      /* Label text looks like "SIZE — Medium — 35cm". Second token is the size. */
      var parts = (label.textContent || '').split(/\u2014|—|-/).map(function (p) { return p.trim(); }).filter(Boolean);
      if (parts.length >= 2) return parts[1];
    }
    var checked = section.querySelector('variant-radios input:checked');
    if (checked && checked.value) return checked.value;
    return null;
  }

  /* Find the slide index (within what's currently in the DOM) that
     corresponds to `featuredMediaId`. Returns -1 if not found. */
  function findSlideIndexByMediaId(container, featuredMediaId) {
    if (!container || !featuredMediaId) return -1;
    var slides = container.querySelectorAll('.product__media-item, .product__media-subitem');
    for (var i = 0; i < slides.length; i++) {
      var id = slides[i].getAttribute('data-media-id') || slides[i].getAttribute('data-media-sub-id') || '';
      /* data-media-id is "<sectionId>-<mediaId>" — match either full or suffix */
      if (id === String(featuredMediaId) || id.endsWith('-' + featuredMediaId)) return i;
    }
    return -1;
  }

  function updateSwiperTo(container, targetIndex) {
    if (!container || !container.swiper) return;
    try {
      container.swiper.update();
      var idx = targetIndex >= 0 ? targetIndex : 0;
      container.swiper.slideTo(idx, 0);
    } catch (err) { /* no-op */ }
  }

  function initSection(section) {
    if (section.__odeGalleryFilterInit) return;

    /* Don't init until Swiper has been attached — otherwise slide
       filtering works but swiper.update() is a no-op. Retry on
       the next frame. */
    var mainListEl = section.querySelector('.js-media-list');
    if (mainListEl && !mainListEl.swiper) {
      requestAnimationFrame(function () { initSection(section); });
      return;
    }

    section.__odeGalleryFilterInit = true;

    var sectionId = section.dataset.section || (section.id || '').replace(/^MainProduct-/, '');

    var mainLists = Array.prototype.slice.call(section.querySelectorAll('.js-media-list'));
    var subLists  = Array.prototype.slice.call(section.querySelectorAll('.js-media-sublist'));

    /* Fallback for static (non-swiper) gallery layouts — they render
       `.product__media-list` without `js-media-list`. */
    if (mainLists.length === 0) {
      mainLists = Array.prototype.slice.call(section.querySelectorAll('.product__media-list'));
    }

    var mainCache = [];
    mainLists.forEach(function (ml) {
      mainCache = mainCache.concat(cacheSlides(ml, '.product__media-item'));
    });

    var subCache = [];
    subLists.forEach(function (sl) {
      subCache = subCache.concat(cacheSlides(sl, '.product__media-subitem'));
    });

    /* Nothing to filter — bail gracefully. */
    var hasAnyTag = mainCache.some(function (s) { return s.sizeTag; })
                 || subCache.some(function (s) { return s.sizeTag; });
    if (!hasAnyTag) return;

    /* Studio Ode — desktop-only toggle. When `data-ode-filter-desktop-only`
       is "true" on the section, mobile (<750px) shoppers see every uploaded
       image regardless of variant. The mobile media query mirrors the
       theme's existing 749/750 breakpoint. */
    var desktopOnly = section.getAttribute('data-ode-filter-desktop-only') === 'true';
    var mobileMQ = window.matchMedia('(max-width: 749px)');
    var lastSize = null;
    var lastFeaturedId = null;

    function shouldSkipFilter() {
      return desktopOnly && mobileMQ.matches;
    }

    function restoreAll() {
      /* Re-insert every cached slide back into its swiper-wrapper, in
         original order. Used when the desktop-only toggle is on and we
         drop into the mobile breakpoint. */
      mainCache.forEach(function (s) { reinsertInOrder(s, mainCache); });
      subCache.forEach(function (s) { reinsertInOrder(s, subCache); });
      mainLists.forEach(function (ml) { updateSwiperTo(ml, 0); });
      subLists.forEach(function (sl) { updateSwiperTo(sl, 0); });
    }

    function filter(size, featuredMediaId) {
      lastSize = size;
      lastFeaturedId = featuredMediaId;

      if (shouldSkipFilter()) {
        /* Desktop-only mode on mobile: don't filter out any slides, but
           still scroll to the variant's featured media so the gallery
           swaps to the selected variant's image when the shopper picks
           a new size. (Previously we called restoreAll() which always
           snapped back to slide 0 — visually felt like the variant
           image switch was broken on mobile.) */
        mainCache.forEach(function (s) { reinsertInOrder(s, mainCache); });
        subCache.forEach(function (s) { reinsertInOrder(s, subCache); });
        mainLists.forEach(function (ml) {
          var idx = findSlideIndexByMediaId(ml, featuredMediaId);
          updateSwiperTo(ml, idx >= 0 ? idx : 0);
        });
        subLists.forEach(function (sl) {
          var idx = findSlideIndexByMediaId(sl, featuredMediaId);
          updateSwiperTo(sl, idx >= 0 ? idx : 0);
        });
        return;
      }

      applyFilter(mainCache, size);
      applyFilter(subCache, size);

      /* Re-target each slider to the variant's featured media
         (if present + now visible), otherwise slide 0. */
      mainLists.forEach(function (ml) {
        var idx = findSlideIndexByMediaId(ml, featuredMediaId);
        updateSwiperTo(ml, idx);
      });
      subLists.forEach(function (sl) {
        var idx = findSlideIndexByMediaId(sl, featuredMediaId);
        updateSwiperTo(sl, idx);
      });
    }

    /* ── Initial filter on page load ── */
    var initialSize = readInitialSize(section);
    if (initialSize) {
      /* On load, the variant's featured media is the first slide in the DOM,
         so no explicit target — just slideTo(0). */
      filter(initialSize, null);
    }

    /* ── Variant change ── */
    document.addEventListener('variant:change', function (e) {
      if (!e.detail || !e.detail.variant) return;
      if (sectionId && e.detail.sectionId && e.detail.sectionId !== sectionId) return;

      var size = variantSize(e.detail.variant);
      var featuredId = e.detail.variant.featured_media && e.detail.variant.featured_media.id
        ? e.detail.variant.featured_media.id
        : null;

      /* The theme's variant-radios replaces or reorders media DOM via its
         own `updateMedia()` BEFORE dispatching variant:change. Our cached
         slide references survive because we stored them at page-load time
         and Shopify's updateMedia reorders without recreating nodes (in
         `show_all` mode). A small delay lets the theme's scroll/reorder
         settle before we filter. */
      if (size) {
        setTimeout(function () { filter(size, featuredId); }, 30);
      }
    });

    /* ── Viewport crosses the 750px breakpoint ──
       When desktop-only is enabled, switching DPR/orientation/window
       size between mobile and desktop must re-evaluate the filter so
       the gallery reflects the new mode without a page reload. */
    if (desktopOnly) {
      var onMQChange = function () {
        if (mobileMQ.matches) {
          /* Now mobile — show everything. */
          restoreAll();
        } else if (lastSize) {
          /* Now desktop — re-apply the filter for the current size. */
          filter(lastSize, lastFeaturedId);
        }
      };
      if (typeof mobileMQ.addEventListener === 'function') {
        mobileMQ.addEventListener('change', onMQChange);
      } else if (typeof mobileMQ.addListener === 'function') {
        mobileMQ.addListener(onMQChange); /* Safari < 14 */
      }
    }
  }

  function boot() {
    document.querySelectorAll('[id^="MainProduct-"], section[data-section]').forEach(function (section) {
      if (!section.querySelector('.js-media-list, .js-media-sublist, .product__media-list')) return;
      initSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(boot, 80); });
  } else {
    setTimeout(boot, 80);
  }

  document.addEventListener('shopify:section:load', function () { setTimeout(boot, 80); });
})();
